import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  IconButton,
  Divider,
  Alert,
  Link,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  AccountBalance as WageIcon,
  Warning as WarningIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import { useAvailability, getEmployeeAvailabilityForDay } from '../hooks/useAvailability';
import { useLocations } from '../hooks/useFirestoreData';
import { useNavigate } from 'react-router-dom';
import useEmployeeAssignments from '../hooks/useEmployeeAssignments';

const DAYS_OF_WEEK = [
  { key: 1, label: 'Mon', fullLabel: 'Monday' },
  { key: 2, label: 'Tue', fullLabel: 'Tuesday' },
  { key: 3, label: 'Wed', fullLabel: 'Wednesday' },
  { key: 4, label: 'Thu', fullLabel: 'Thursday' },
  { key: 5, label: 'Fri', fullLabel: 'Friday' },
  { key: 6, label: 'Sat', fullLabel: 'Saturday' },
  { key: 0, label: 'Sun', fullLabel: 'Sunday' },
];

// Helper function to convert 24-hour time to 12-hour format
function formatTime12Hour(time24) {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

function AvailabilityDayChip({ day, availability, conflicts = [] }) {
  const hasConflict = conflicts.includes(day.key);
  
  const getAvailabilityDisplay = () => {
    if (!availability) return { text: 'Not Set', color: 'default' };
    
    switch (availability.type) {
      case 'all_day_available':
        return { text: 'All Day', color: 'success' };
      case 'all_day_not_available':
        return { text: 'Unavailable', color: 'error' };
      case 'available':
        return { 
          text: `${formatTime12Hour(availability.startTime || '09:00')} - ${formatTime12Hour(availability.endTime || '17:00')}`, 
          color: 'success' 
        };
      case 'not_available':
        return { 
          text: `Unavailable ${formatTime12Hour(availability.startTime || '09:00')} - ${formatTime12Hour(availability.endTime || '17:00')}`, 
          color: 'warning' 
        };
      default:
        return { text: 'Available', color: 'success' };
    }
  };

  const { text, color } = getAvailabilityDisplay();

  return (
    <Box sx={{ textAlign: 'center', mb: 1 }}>
      <Typography variant="caption" display="block" fontWeight="bold">
        {day.label}
      </Typography>
      <Chip
        label={text}
        color={color}
        size="small"
        variant={hasConflict ? 'filled' : 'outlined'}
        icon={hasConflict ? <WarningIcon fontSize="small" /> : undefined}
        sx={{ 
          fontSize: '0.7rem',
          height: 'auto',
          '& .MuiChip-label': {
            padding: '4px 8px',
            whiteSpace: 'normal',
            lineHeight: 1.2,
          }
        }}
      />
      {availability?.reason && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {availability.reason}
        </Typography>
      )}
    </Box>
  );
}

export default function EmployeeQuickView({ 
  employee, 
  open, 
  onClose, 
  locationId, 
  locations = [], 
  scheduleConflicts = [] 
}) {
  const navigate = useNavigate();
  const { availability } = useAvailability(locationId, employee?.id);
  // Also try to load tenant locations to resolve friendly names when possible
  const tenantLocations = useLocations(locationId, () => {});

  // Load employee assignments via hook at component top-level (never conditional)
  const { nested: flattenedNested } = useEmployeeAssignments(employee?.id);

  const employeeName = employee?.name || (employee?.firstName && employee?.lastName 
    ? `${employee.firstName} ${employee.lastName}`.trim() 
    : 'Unknown Employee');
  
  // Get employee locations
  const employeeLocations = useMemo(() => {
    if (!employee) return [];
    const locs = [];

  // Prefer flattened reconstructed assignments from hook when available
  const effectiveAssignments = (flattenedNested && Object.keys(flattenedNested).length) ? flattenedNested : (employee.assignments || {});
    
    // Helper to resolve name from either prop locations or tenantLocations
    const resolveLocation = (locId) => {
      const fromProp = (locations || []).find(loc => loc.id === locId);
      if (fromProp) return { ...fromProp };
      const fromTenant = (tenantLocations || []).find(loc => loc.id === locId);
      if (fromTenant) return { ...fromTenant };
      return { id: locId, name: locId };
    };

    // Primary location
    if (employee.locationId) {
      const primaryLoc = resolveLocation(employee.locationId);
      locs.push({ ...primaryLoc, isPrimary: true });
    }
    
    // Additional assignments from flattened or legacy nested assignments
    Object.keys(effectiveAssignments).forEach(locId => {
      if (locId !== employee.locationId) {
        const addLoc = resolveLocation(locId);
        locs.push({ ...addLoc, isPrimary: false });
      }
    });
    
    return locs;
  }, [employee, locations, tenantLocations, flattenedNested]);

  // Get weekly availability
  const weeklyAvailability = useMemo(() => {
    return DAYS_OF_WEEK.map(day => ({
      ...day,
      availability: getEmployeeAvailabilityForDay(availability, employee?.id, day.key)
    }));
  }, [availability, employee?.id]);

  const handleNavigateToProfile = () => {
    if (employee?.id) {
      navigate(`/employees/${employee.id}`);
      onClose();
    }
  };

  const handleNavigateToAvailability = () => {
    navigate('/employee-availability');
    onClose();
  };

  if (!employee) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}>
              {employeeName.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h6">{employeeName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {employee.role || 'Staff Member'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon /> Contact Information
                </Typography>
                
                {employee.phone && (
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <PhoneIcon fontSize="small" />
                    <Link href={`tel:${employee.phone}`} underline="hover">
                      {employee.phone}
                    </Link>
                  </Box>
                )}
                
                {employee.email && (
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <EmailIcon fontSize="small" />
                    <Link href={`mailto:${employee.email}`} underline="hover">
                      {employee.email}
                    </Link>
                  </Box>
                )}

                {!employee.phone && !employee.email && (
                  <Typography variant="body2" color="text.secondary">
                    No contact information available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Locations & Wages */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WageIcon /> Work Details
                </Typography>
                
                <Typography variant="subtitle2" gutterBottom>Locations:</Typography>
                <Box mb={2}>
                  {employeeLocations.map(loc => (
                    <Chip
                      key={loc.id}
                      label={`${loc.name}${loc.isPrimary ? ' (Primary)' : ''}`}
                      variant={loc.isPrimary ? 'filled' : 'outlined'}
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                </Box>

                {employee.hourlyWage && (
                  <Typography variant="body2">
                    <strong>Hourly Wage:</strong> ${employee.hourlyWage}/hr
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Weekly Availability */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon /> Weekly Availability
                </Typography>

                {scheduleConflicts.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This employee has {scheduleConflicts.length} scheduling conflict(s) this week
                  </Alert>
                )}

                <Grid container spacing={1}>
                  {weeklyAvailability.map(day => (
                    <Grid item xs={12/7} key={day.key}>
                      <AvailabilityDayChip
                        day={day}
                        availability={day.availability}
                        conflicts={scheduleConflicts}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Quick Actions */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>Quick Actions</Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<PersonIcon />}
                endIcon={<LaunchIcon />}
                onClick={handleNavigateToProfile}
              >
                View Full Profile
              </Button>
              <Button
                variant="outlined"
                startIcon={<ScheduleIcon />}
                endIcon={<LaunchIcon />}
                onClick={handleNavigateToAvailability}
              >
                Manage Availability
              </Button>
              {employee.phone && (
                <Button
                  variant="outlined"
                  startIcon={<PhoneIcon />}
                  href={`tel:${employee.phone}`}
                >
                  Call Employee
                </Button>
              )}
              {employee.email && (
                <Button
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  href={`mailto:${employee.email}`}
                >
                  Send Email
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
